import axiosInstance, {
  getAccessToken,
  onAccessTokenChange,
} from "../../axios/index";
import {
  SET_NOTIFICATIONS,
  CONNECT,
  DISCONNECT,
} from "../types/notificationsTypes";
import { requestFailed } from "./feedbackActionCreators";
import { alertError } from "../../utils/feedback";
import { ERROR_MESSAGE } from "../types/chatTypes";
import io from "socket.io-client";
import { getOrganizatonMetricsById } from "./organizationProfileCreators";

export const setAllNotifications = (notifcations) => ({
  type: SET_NOTIFICATIONS,
  payload: notifcations,
});

export const disconnectEvent = () => ({
  type: DISCONNECT,
});

export const connectionEvent = () => ({
  type: CONNECT,
});

export const errorMessage = (err) => {
  const language = localStorage.getItem("i18nextLng");
  let errorM;
  console.log("errorMessage : ", err);
  if (language === "en") {
    errorM =
      err?.message || (err?.message === null ? undefined : "Request failed");
  } else if (language === "fr") {
    errorM =
      err?.message_fr ||
      err?.message ||
      (err?.message_fr === null ? undefined : "Request failed");
  }
  if (typeof err == "string") errorM = err;

  // The last caller of the sign-out arm was the notif *failure* path above,
  // where `err` is a server-supplied notification payload rather than an auth
  // error at all (F-60). The socket's own "token expired" handling stays where
  // it is, in the server_error listener: it is not an HTTP path, and it is the
  // Part III fix.
  alertError(errorM);

  return {
    type: ERROR_MESSAGE,
    payload: "",
  };
};

export const fetchAllNotifications = () => {
  return async (dispatch) => {
    try {
      const res = await axiosInstance.get(`/auth/admin_recent_notifications`);
      const items = res?.data?.notifications;
      if (items && items.length > 0) {
        let called = false;

        for (let item of items) {
          if (!item?.is_read && item?.existing_report && !called) {
            const orgaId = localStorage.getItem("orgaId");
            const normalizedOrgaId = orgaId ? String(orgaId).trim() : "";
            if (
              normalizedOrgaId &&
              normalizedOrgaId !== "null" &&
              normalizedOrgaId !== "undefined"
            ) {
              dispatch(getOrganizatonMetricsById(normalizedOrgaId));
            }
            called = true;
            break; // stop loop once triggered
          }
        }
      }
      dispatch(setAllNotifications(items));
    } catch (error) {
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

export const updateNotification = (id) => {
  return async (dispatch) => {
    try {
      const res = await axiosInstance.put(
        `/auth/update_admin_notification/${id}`,
        {
          is_read: true,
        }
      );
      dispatch(fetchAllNotifications());
    } catch (error) {
      dispatch(requestFailed(error?.response?.data));
    }
  };
};

// The event BE-8 answers on a live connection: it re-verifies the token the
// event carries and keeps or drops the socket on the result. Client and server
// have to agree on the string, the same way FE-2 and BE-3 agree on
// "session ended".
const TOKEN_UPDATE = "token_update";

// Whether a component has asked for notifications yet. The socket outlives all
// of them - InsideLayout remounts on every private-route navigation (F-28) - so
// this is module state rather than anything a mount owns.
let socketStarted = false;
// The credential handed to the server on the last connection attempt, and the
// one it refused. Part III #2's guarantee is that a dead token is never replayed
// by socket.io's reconnect loop; the token is read per attempt now instead of
// being captured, so the guarantee lives in these two rather than in a cleared
// socket.auth.
let presentedToken = null;
let rejectedToken = null;

const socketCredential = () => {
  const token = getAccessToken();
  return token && token !== rejectedToken ? token : null;
};

let socket = io(`${process.env.REACT_APP_API_URL_NOTIFICATION}`, {
  transports: ["websocket"],
  autoConnect: false, // Prevent automatic connection on initialization
  // A function, not an object: socket.io calls it before every connection
  // attempt, so a reconnect presents whatever token is current at that moment.
  // The component used to read it once at mount and hand it over, which after
  // KC-1 means any reconnect more than 300s later re-authenticates with an
  // expired credential (F-28).
  auth: (cb) => {
    presentedToken = socketCredential();
    cb({ token: presentedToken });
  },
});

// Where a refreshed token is noticed, whichever tab produced it: a 401 refresh
// here, the boot exchange in App.js, or another tab's refresh arriving over the
// FE-5 channel, or the scheduled renewal that now runs alongside them.
// Subscribing to the token itself rather than to this tab's requests is what
// covers all four - none of them carries local HTTP traffic, so a passive tab
// used to keep presenting the old credential until it happened to make a call.
//
// The schedule is what keeps this connection up. Until it existed a token was
// only ever renewed by a 401, an idle tab produced none, and BE-8 swept the
// socket ~300s in - so a report finishing during the wait was delivered to
// nobody. See the proactive refresh in axios/index.js.
const syncSocketToken = () => {
  if (!socketStarted) {
    return;
  }

  const token = socketCredential();
  if (!token || token === presentedToken) {
    return;
  }

  if (socket.connected) {
    // The live connection re-authenticates in place: BE-8 verifies this token
    // and keeps or drops the socket on the result, so a connection is never
    // left authorised by one that has aged out (F-17).
    presentedToken = token;
    socket.emit(TOKEN_UPDATE, { token });
    return;
  }

  // Down since the last expiry. The refresh that just landed is the credential
  // it was waiting for, and auth reads it on the way back up.
  socket.connect();
};

onAccessTokenChange(syncSocketToken);

export const initializeSocketListeners = () => async (dispatch) => {
  socketStarted = true;

  // React remounts and token refreshes must not accumulate duplicate handlers.
  socket.off("connect");
  socket.off("notif");
  socket.off("server_error");
  socket.off("connect_error");
  socket.off("disconnect");

  socket.on("connect", () => {
    dispatch(connectionEvent());
    // A socket emit is fire-and-forget: an event broadcast while this connection
    // was down is gone, not queued. The row behind it is in mongo either way, so
    // coming back up is the moment to ask what was missed - otherwise the user
    // keeps a signed-in page that silently stops being current, and only a manual
    // reload fixes it. Observed over a laptop suspend: the socket died at 21:31,
    // a report finished at 22:14 and emitted to nobody, the socket came back at
    // 04:23 and the notification still did not appear until the page was
    // reloaded by hand an hour later.
    //
    // This is the same call the mounting component makes, so the first connect
    // repeats one GET. Every connect after that is a reconnect - a suspend, a
    // tunnel, a dropped wifi, a redeployed pod - and that is the one that matters.
    dispatch(fetchAllNotifications());
  });

  socket.on("notif", (res) => {
    const status = String(res?.status || "").toLowerCase();
    if (status !== "success" && status !== "failure") {
      console.warn("Ignored notification with unsupported status", status);
      return;
    }

    // Both terminal outcomes create a persisted notification row.
    dispatch(fetchAllNotifications());
    if (status === "failure") {
      dispatch(errorMessage(res));
    }
  });

  socket.on("server_error", (error) => {
    // The server hangs up on an expired token, but socket.io keeps reconnecting
    // with the same dead credentials and would re-raise this long after the user
    // signed out - including on top of the next, valid session.
    if (error?.error_ === "token expired") {
      rejectedToken = presentedToken;
      socket.disconnect();
      // If a refresh has already replaced the credential the server just
      // refused, come straight back up with it; otherwise stay down until one
      // does. Either way the refused token is never presented again, which is
      // what stops the reconnect loop.
      syncSocketToken();
      // Deliberately not errorMessage: an access token ageing out mid-connection
      // is routine at 300s rather than evidence the session is over, and it is
      // not even worth a toast. Treating it as a sign-out is the production bug
      // Part III fixed, reopened on a valid session (F-17, H-2). The HTTP
      // interceptor is the only thing that decides a session has ended.
      return;
    }
    dispatch(errorMessage(error));
  });

  socket.on("connect_error", (error) => {
    console.error("Unable to establish the notification websocket", error);
    dispatch(disconnectEvent(error));
  });

  socket.on("disconnect", (error) => {
    //alert("disconnected socket")
    console.log("disconnected from the socket server");
    dispatch(disconnectEvent(error));
  });

  if (socket.connected) {
    // Already up, and a remount must not churn a working connection - this runs
    // again on every private-route navigation (F-28). It is still the right
    // moment to check the connection is holding the current token.
    syncSocketToken();
    return;
  }

  if (socketCredential()) {
    socket.connect();
  }
  // No usable credential yet, or the current one has already been refused:
  // connecting anyway only earns a "Token not found" and a toast. The request
  // interceptor above brings the socket up as soon as a fresh token lands.
};

export const socketDisconnect = async () => {
  socket.off("connect");
  socket.off("notif");
  socket.off("server_error");
  socket.off("connect_error");
  socket.off("disconnect");
  socket.disconnect();
  socketStarted = false;
  presentedToken = null;
  rejectedToken = null;
};
